import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-checkbox WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, CheckboxWebElement.ts.
 */
export class CheckboxWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>containerReadonly</code> property.
   * Specifies whether an ancestor container, like oj-c-form-layout, is readonly.
   * @return The value of <code>containerReadonly</code> property.
   *
   */
  public getContainerReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('containerReadonly');
  }

  /**
   * Gets the value of <code>columnSpan</code> property.
   * Specifies how many columns this component should span.
   * @return The value of <code>columnSpan</code> property.
   *
   */
  public getColumnSpan(): Promise<number> {
    return this.getProperty<number>('columnSpan');
  }

  /**
   * Gets the value of <code>disabled</code> property.
   * Specifies whether the component is disabled.
   * @return The value of <code>disabled</code> property.
   *
   */
  public getDisabled(): Promise<boolean> {
    return this.getProperty<boolean>('disabled');
  }

  /**
   * Gets the value of <code>displayOptions</code> property.
   * Display options for auxiliary content that describes whether or not it should be displayed.
   * @return The value of <code>displayOptions</code> property.
   *
   */
  public getDisplayOptions(): Promise<DisplayOptions> {
    return this.getProperty<DisplayOptions>('displayOptions');
  }

  /**
   * Gets the value of <code>help</code> property.
   * Form component help information.
   * @return The value of <code>help</code> property.
   *
   */
  public getHelp(): Promise<Help> {
    return this.getProperty<Help>('help');
  }

  /**
   * Gets the value of <code>helpHints</code> property.
   * The helpHints object contains a definition property, sourceText property, and a source property.
   * @return The value of <code>helpHints</code> property.
   *
   */
  public getHelpHints(): Promise<HelpHints> {
    return this.getProperty<HelpHints>('helpHints');
  }

  /**
   * Sets the value of <code>messagesCustom</code> property.
   * List of custom component messages
   * @param messagesCustom The value to set for <code>messagesCustom</code>
   *
   */
  public changeMessagesCustom(messagesCustom: Array<MessagesCustom>): Promise<void> {
    return this.setProperty<Array<MessagesCustom>>('messagesCustom', messagesCustom);
  }

  /**
   * Gets the value of <code>messagesCustom</code> property.
   * List of custom component messages
   * @return The value of <code>messagesCustom</code> property.
   *
   */
  public getMessagesCustom(): Promise<Array<MessagesCustom>> {
    return this.getProperty<Array<MessagesCustom>>('messagesCustom');
  }

  /**
   * Gets the value of <code>readonly</code> property.
   * Whether the component is readonly
   * @return The value of <code>readonly</code> property.
   *
   */
  public getReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('readonly');
  }

  /**
   * Gets the value of <code>readonlyUserAssistanceShown</code> property.
   * Specifies which user assistance types should be shown when the component is readonly.
   * @return The value of <code>readonlyUserAssistanceShown</code> property.
   *
   */
  public getReadonlyUserAssistanceShown(): Promise<string> {
    return this.getProperty<string>('readonlyUserAssistanceShown');
  }

  /**
   * Gets the value of <code>required</code> property.
   * Specifies whether or not the component is required.
   * @return The value of <code>required</code> property.
   *
   */
  public getRequired(): Promise<boolean> {
    return this.getProperty<boolean>('required');
  }

  /**
   * Gets the value of <code>userAssistanceDensity</code> property.
   * Specifies the density of the form component's user assistance presentation.
   * @return The value of <code>userAssistanceDensity</code> property.
   *
   */
  public getUserAssistanceDensity(): Promise<string> {
    return this.getProperty<string>('userAssistanceDensity');
  }

  /**
   * Gets the value of <code>requiredMessageDetail</code> property.
   * Overrides the default Required error message.
   * @return The value of <code>requiredMessageDetail</code> property.
   *
   */
  public getRequiredMessageDetail(): Promise<string> {
    return this.getProperty<string>('requiredMessageDetail');
  }

  /**
   * Gets the value of <code>valid</code> property.
   * Specifies how the valid state of the component
   * @return The value of <code>valid</code> property.
   *
   */
  public getValid(): Promise<string> {
    return this.getProperty<string>('valid');
  }

  /**
   * Sets the value of <code>value</code> property.
   * The value of the component.
   * @param value The value to set for <code>value</code>
   *
   */
  public changeValue(value: boolean): Promise<void> {
    return this.setProperty<boolean>('value', value);
  }

  /**
   * Gets the value of <code>value</code> property.
   * The value of the component.
   * @return The value of <code>value</code> property.
   *
   */
  public getValue(): Promise<boolean> {
    return this.getProperty<boolean>('value');
  }
}
export interface DisplayOptions {
  /**
   *
   */
  messages: string;
}
export interface Help {
  /**
   *
   */
  instruction: string;
}
export interface HelpHints {
  /**
   *
   */
  definition: string;
  /**
   *
   */
  source: string;
  /**
   *
   */
  sourceText: string;
}
export interface MessagesCustom {
  /**
   *
   */
  summary: string;
  /**
   *
   */
  detail: string;
  /**
   *
   */
  severity: string;
}
